import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { jsPDF } from 'jspdf';

const ExamUnionFaculties = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [userId] = useState(authState.user._id);

  const [faculties, setExamUnionFaculties] = useState([]);
  const consultantFields = faculties.length > 0
  ? Object.keys(faculties[0])
  : [];

  const [fetchData, { loading }] = useFetch();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tags, setTags] = useState('');
  const [consultantType, setConsultantType] = useState('');
  const [consultantTypeHead, setConsultantTypeHead] = useState('');
  const consultantTypeInitialized = useRef(false);
  const [selectedConsultantId, setSelectedConsultantId] = useState(null);
  const [type, setType] = useState(1);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [searchName, setSearchName] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchFirm, setSearchFirm] = useState('');
  const [sortField, setSortField] = useState("dateCreated");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchExamUnionFaculties = useCallback(() => {
    const config = {
      url: "/consultants/list",
      method: "post",
      headers: { Authorization: authState.token },
      data: { type: type }
    };
    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response && Array.isArray(response.consultants)) {
          setExamUnionFaculties(response.consultants);
        } else {
          console.error("Invalid response format: faculties not found in response");
        }
      })
      .catch(error => console.error("Error fetching faculties:", error));
  }, [authState.token, fetchData, type]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchExamUnionFaculties();
  }, [authState.isLoggedIn, fetchExamUnionFaculties]);

  const handleCallClick = (contactNumber) => window.location.href = `tel:${contactNumber}`;
  const handleWhatsAppClick = (contactNumber) => window.open(`https://wa.me/${contactNumber}`, '_blank');
  const handleEmailClick = (contactEmail) => window.location.href = `mailto:${contactEmail}`;

  const allowedSortFields = [
    { value: "name", label: "Name" },
    { value: "city", label: "City" },
    { value: "firmName", label: "Business Name" },
    { value: "country", label: "Country" },
    { value: "workStatus", label: "Work Status" },
    // Add more if needed
  ];

  const sortedExamUnionFaculties = [...faculties].sort((a, b) => {
      const A = a[sortField];
      const B = b[sortField];

      // numeric comparison
      if (!isNaN(A) && !isNaN(B)) {
        return sortOrder === "asc" ? A - B : B - A;
      }

      // date comparison
      if (A instanceof Date || B instanceof Date || !isNaN(Date.parse(A))) {
        return sortOrder === "asc"
          ? new Date(A) - new Date(B)
          : new Date(B) - new Date(A);
      }

      // string comparison
      return sortOrder === "asc"
        ? String(A).localeCompare(String(B))
        : String(B).localeCompare(String(A));
  });

  const filteredExamUnionFaculties = sortedExamUnionFaculties.filter(consultant =>
    consultant.name.toLowerCase().includes(searchName.toLowerCase()) &&
    (searchCity === '' || consultant.city.toLowerCase().includes(searchCity.toLowerCase())) &&
    (searchFirm === '' || consultant.firmName.toLowerCase().includes(searchFirm.toLowerCase()))
  );

  // const buttonStyle = "mx-1 my-1 px-3 py-1 text-xs sm:text-sm rounded bg-green-500 text-white hover:bg-green-600 transition";

  const generateConsultantPDF = (consultant) => {
    const pdf = new jsPDF({ unit: "pt" });

    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(14);

    pdf.text("Consultant Details", 40, 40);
    pdf.setFontSize(11);

    let y = 70;

    const addLine = (label, value) => {
      pdf.text(`${label}: ${value || "N/A"}`, 40, y);
      y += 20;
    };

    addLine("Name", consultant.name);
    addLine("Business Name", consultant.firmName);
    addLine("City", consultant.city);
    addLine("Country", consultant.country);
    addLine("Website", consultant.website);
    addLine("Personal Counsellor", consultant.isCounsellor ? "Yes" : "No");
    addLine("Company Consultant", consultant.isConsultant ? "Yes" : "No");
    addLine("Brief Bio", consultant.briefBio);
    addLine("Work Status", consultant.workStatus);

    y += 20;
    pdf.setFontSize(13);
    pdf.text("Reviews", 40, y);
    y += 20;

    pdf.setFontSize(11);

    if (consultant.reviews && consultant.reviews.length > 0) {
      consultant.reviews.forEach((review, index) => {
        pdf.text(
          `${index + 1}. ${review.reviewerName} | ${new Date(
            review.reviewDate
          ).toLocaleDateString()} | ${review.reviewerCity}`,
          40,
          y
        );
        y += 18;

        if (y > 750) {
          pdf.addPage();
          y = 40;
        }
      });
    } else {
      pdf.text("No reviews available.", 40, y);
    }

    pdf.save(`${consultant.name}-profile.pdf`);
  };


  return (
    <div className="bg-black text-white text-sm min-h-screen">
      <header className="sticky top-0 z-50 bg-black px-4 py-3 flex flex-col items-center justify-center shadow-md border-b border-blue-400">
        <h1 className="text-xl text-yellow-200 mt-1 text-center">Counsellors and ExamUnionFaculties@Think5Whys </h1>
      </header>

      <div className="my-2 p-2">
        <div className="bg-black shadow-lg rounded-lg p-4 sm:p-6">
          <div className="mb-2 py-4 px-2 grid grid-cols-1 lg:grid-cols-3 gap-4 place-items-center bg-gray-700 rounded-md">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              style={{width: "100%"}}
              className="w-full p-3 bg-gray-800 border-2 border-green-400 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500 text-sm"
            />
            <input
              type="text"
              placeholder="Search by city..."
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              style={{width: "100%"}}              
              className="w-full p-3 bg-gray-800 border-2 border-green-400 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500 text-sm"
            />
            <input
              type="text"
              placeholder="Search by firm..."
              value={searchFirm}
              onChange={(e) => setSearchFirm(e.target.value)}
              style={{width: "100%"}}                  
              className="w-full p-3 bg-gray-800 border-2 border-green-400 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500 text-sm"
            />
          </div>

          <div className="flex flex-row items-left gap-4 bg-gray-900 p-3 rounded-lg border border-green-500 mb-4">
            <div className="flex items-center gap-2 w-full sm:w-auto mb-2 sm:mb-0">
              <label className="text-white text-sm">Sort By:</label>

              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                className="p-2 h-10 bg-black border border-green-400 rounded text-white text-sm"
              >
                {allowedSortFields.map(field => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>


            {/* SORT ORDER */}
            <div
              className="cursor-pointer flex items-center gap-2 bg-black border border-green-400 px-3 py-2 rounded text-white text-sm hover:bg-green-700 transition"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            >
              <span>Order:</span>
              {sortOrder === "asc" ? (
                <span className="text-lg">⬆️</span>
              ) : (
                <span className="text-lg">⬇️</span>
              )}
            </div>
          </div>

          {faculties && faculties.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center bg-black border-b border-gray-500 pb-4 mb-4">
              <h3 className="text-lg font-semibold text-white text-center sm:text-left mb-2 sm:mb-0">
                {filteredExamUnionFaculties.length} {consultantTypeHead} { (searchName || searchCity || searchFirm) ? 'matching ' : ''}ExamUnionFaculties/Counsellors Found!
              </h3>
              <Link
                to="/consultants/add"
                className="flex items-center px-4 py-2 text-sm bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 transition"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Register as Faculty
              </Link>
            </div>
          )}

          {loading ? (
            <Loader />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 bg-black">
              {filteredExamUnionFaculties.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[200px] text-yellow-300 col-span-full">
                  <h3 className="text-2xl font-bold text-orange-400">{ (searchName || searchCity || searchFirm) ? 'No matching records found!' : 'No Record Found!'}</h3>
                </div>
              ) : (
                filteredExamUnionFaculties.map(consultant => (
                  <div key={consultant._id} className="bg-black border text-sm border-gray-300 rounded-lg shadow-lg hover:shadow-xl transition p-4 flex flex-col items-center">
                    <div className="w-24 sm:w-40 h-24 sm:h-40 rounded-full border-2 border-green-400 shadow-md flex items-center justify-center bg-gray-700 overflow-hidden">
                      {imageErrors.has(consultant._id) ? (
                        <span className="text-2xl sm:text-4xl">🖼️</span>
                      ) : (
                        <img 
                          src={`/api/downloads/consultant/${consultant.userCode}.png`}
                          alt={consultant.name}
                          className="w-full h-full object-cover"
                          onError={() => setImageErrors(prev => new Set([...prev, consultant._id]))}
                        />
                      )}
                    </div>

                    <div className="mt-4 text-center w-full">
                      <h3 className="text-m font-semibold text-white">{consultant.name}</h3>
                      <p className="text-sm text-white mt-1">Rating: {consultant.rating} ({consultant.ratingCount})</p>
                      <p className="text-white mt-2">Charges: {consultant.charges}</p>
                      <div className="grid grid-cols-4 gap-2 justify-center rounded-xl border-2 border-green-400 mt-2 py-4 place-items-center w-full">
                      <button 
                        onClick={() => handleCallClick(consultant.mobile)} 
                        className="w-full bg-black text-white px-4 py-2 rounded-md border-2 border-green-400 hover:text-green-400 shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <span className="text-lg">📞</span>
                        <span className="hidden sm:inline">Call</span>
                      </button>
                      
                      <button 
                        onClick={() => handleWhatsAppClick(consultant.mobile)}  
                        className="w-full bg-black text-white px-4 py-2 rounded-md border-2 border-green-400 hover:text-green-400 shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <span className="text-lg">💬</span>
                        <span className="hidden sm:inline">WhatsApp</span>
                      </button>
                      
                      <button 
                        onClick={() => handleEmailClick(consultant.email)}  
                        className="w-full bg-black text-white px-4 py-2 rounded-md border-2 border-green-400 hover:text-green-400 shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <span className="text-lg">✉️</span>
                        <span className="hidden sm:inline">Email</span>
                      </button>
                      
                      <button
                        className="w-full bg-black border-2 border-green-400 hover:text-green-400 shadow-lg px-4 py-2 rounded-md flex items-center justify-center text-sm sm:text-base"
                        onClick={() => setSelectedConsultantId(consultant._id)}
                        title="View Details"
                      >
                        <i className="fa-solid fa-eye text-lg"></i>
                        <span className="hidden sm:inline ml-1">View</span>
                      </button>
                    </div>
                    

                    {selectedConsultantId === consultant._id && (
                      <div className="text-sm mt-4 p-4 bg-black border border-gray-400 rounded-lg w-full">
                        <h2 className="text-sm font-semibold text-yellow-200">Details</h2>
                        <p><strong>Name:</strong> {consultant.name}</p>
                        <p><strong>Business Name:</strong> {consultant.firmName}</p>
                        <p><strong>City:</strong> {consultant.city}</p>
                        <p><strong>Country:</strong> {consultant.country}</p>
                        <p><strong>Website:</strong> {consultant.website}</p>
                        <p><strong>Personal Counsellor:</strong> {consultant.isCounsellor ? "Yes" : "No"}</p>
                        <p><strong>Company Consultant:</strong> {consultant.isConsultant ? "Yes" : "No"}</p>
                        <p><strong>Brief Bio:</strong> {consultant.briefBio}</p>
                        <p><strong>Client List:</strong> {consultant.clientList}</p>
                        <p><strong>Work Status:</strong> {consultant.workStatus}</p>

                        <h3 className="text-yellow-200 text-sm font-bold mt-4 mb-2">Reviews</h3>
                        {consultant.reviews && consultant.reviews.length > 0 ? (
                          consultant.reviews.map((review, index) => (
                            <div key={index} className="bg-black border border-gray-400 p-3 rounded-lg mb-2 shadow-md w-full">
                              <p><strong>Name:</strong> {review.reviewerName}</p>
                              <p><strong>Date:</strong> {new Date(review.reviewDate).toLocaleDateString()}</p>
                              <p><strong>City:</strong> {review.reviewerCity}</p>
                            </div>
                          ))
                        ) : (
                          <p className="text-yellow-200">No reviews available.</p>
                        )}

                        <Tooltip text="Download PDF" position="top">
                          <span
                            className="text-green-500 cursor-pointer hover:text-green-700 transition"
                            onClick={() => generateConsultantPDF(consultant)}
                          >
                            <i className="fa-solid fa-download"></i>
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <footer className="sticky bg-black border-t border-blue-400 text-white py-2 px-4 mt-2">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left space-y-4 md:space-y-0">
          <p className="text-xs sm:text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions.</p>
          <div className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-6 text-xs sm:text-sm">
            <Link to="/about-us" className="text-white hover:text-purple-200 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-purple-200 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-purple-200 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-purple-200 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default ExamUnionFaculties;